import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export function useUserImage(user, kind) {
  const [src, setSrc] = useState("");
  const stamp = user?.[`${kind}_at`];
  useEffect(() => {
    if (!stamp) { setSrc(""); return undefined; }
    let url = "";
    api.get(`/users/${user.id}/image/${kind}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc(""));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [user?.id, kind, stamp]);
  return src;
}

const norm = (s) => (s || "").trim().toLowerCase();
const store = { dir: {}, subs: new Set(), timer: null, loading: false };
const loadDir = () => {
  if (store.loading || document.hidden) return;
  store.loading = true;
  api.get("/team/photos").then((r) => { store.dir = Object.fromEntries(r.data.map((u) => [norm(u.name), u])); store.subs.forEach((f) => f(store.dir)); })
    .catch(() => {}).finally(() => { store.loading = false; });
};
export const refreshPhotoDirectory = loadDir;
export const isOnlineNow = (u) => !!u?.last_seen && Date.now() - new Date(u.last_seen).getTime() < 60000;

export function usePhotoDirectory() {
  const [dir, setDir] = useState(store.dir);
  useEffect(() => {
    store.subs.add(setDir);
    if (!store.timer) { loadDir(); store.timer = setInterval(loadDir, 30000); }
    return () => { store.subs.delete(setDir); if (!store.subs.size) { clearInterval(store.timer); store.timer = null; } };
  }, []);
  return dir;
}

export function PersonAvatar({ name, size = 22, fallback = null }) {
  const dir = usePhotoDirectory();
  const u = dir[norm(name)];
  if (!u) return fallback ?? <Avatar3D user={{ name }} size={size} />;
  return <Avatar3D user={u} size={size} online={isOnlineNow(u)} testid={`person-photo-${norm(name).replace(/\W+/g, "-")}`} />;
}

export function PersonChip({ name, size = 22 }) {
  if (!name) return <span>—</span>;
  return (
    <span data-testid="person-chip" className="inline-flex items-center gap-1.5 align-middle">
      <PersonAvatar name={name} size={size} /><span>{name}</span>
    </span>
  );
}

export function Avatar3D({ user, size = 36, testid, online = false }) {
  const src = useUserImage(user, "avatar");
  const dot = Math.max(8, Math.round(size * 0.28));
  return (
    <span className="relative inline-flex shrink-0" style={{ width: size, height: size }}>
      <span data-testid={testid} className="avatar-3d flex h-full w-full items-center justify-center overflow-hidden rounded-full font-bold" style={{ fontSize: size * 0.4 }}>
        {src ? <img src={src} alt={user?.name} className="h-full w-full object-cover" /> : user?.name?.[0]}
      </span>
      {online && <span data-testid="online-dot" title="Online now" className="online-dot absolute bottom-0 right-0 rounded-full border-2 border-white bg-[#22C55E]" style={{ width: dot, height: dot }} />}
    </span>
  );
}
